import { FormGroup, IconName, Intent, Text } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";
import {
  GithubRefType,
  OrgId,
  VcsConnectionType,
  sentenceCase,
  vcsTypeToDisplayName,
} from "@hex/common";
import React, { useCallback, useEffect } from "react";
import styled from "styled-components";

import { GithubRef } from "../../generated/graphqlTypes";
import {
  HexButton,
  HexCallout,
  HexInputGroup,
  HexMenuItem,
  HexTooltip,
} from "../../hex-components";
import {
  GitBranchIcon,
  SingleChevronDownIcon,
  TickSmallIcon,
  WarningIcon,
} from "../icons/CustomIcons";

import {
  AddContainer,
  AddIcon,
  InnerButtonContainer,
  SHORT_SHA_LENGTH,
  SelectButton,
  StyledSpinner,
} from "./AddRepoDialog";
import { useGetRefsQuery } from "./queries.generated";

const REF_TYPES = [
  GithubRefType.BRANCH,
  GithubRefType.TAG,
  GithubRefType.COMMIT,
];

export const REF_TYPE_ICON_MAP: Record<GithubRefType, IconName | JSX.Element> =
  {
    [GithubRefType.BRANCH]: <GitBranchIcon />,
    [GithubRefType.TAG]: "tag",
    [GithubRefType.COMMIT]: "git-commit",
  };

const MAX_REFS = 100;

export interface RefSelectorProps {
  orgId: OrgId;
  repoName?: string;
  repoOwner?: string;
  defaultBranch?: string;
  repoError: boolean;
  repoId?: number | null;
  repoLoading: boolean;
  selectedRefType: GithubRefType;
  selectedRef?: GithubRef;
  setSelectedRef: (ref: GithubRef | undefined) => void;
  setSelectedRefType: (type: GithubRefType) => void;
  refFetchError: boolean;
  refDataLoading: boolean;
  connectionType: VcsConnectionType;
}

const SelectContainer = styled.div`
  display: flex;
`;

const RefTypeButton = styled(HexButton)`
  margin-right: 7px;
`;

const RightSelectContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const CommitStatusContainer = styled.div`
  margin-top: 5px;
  margin-left: 7px;
`;

const NoBranchesCallout = styled(HexCallout)`
  margin-top: 12px;
`;

const SuccessIcon = styled(TickSmallIcon)`
  color: ${({ theme }) => theme.intent.SUCCESS};
`;

const ErrorIcon = styled(WarningIcon)`
  color: ${({ theme }) => theme.intent.DANGER};
`;

export const RefSelector: React.FunctionComponent<RefSelectorProps> =
  React.memo(function RefSelector({
    connectionType,
    defaultBranch,
    orgId,
    refDataLoading,
    refFetchError,
    repoError,
    repoId,
    repoLoading,
    repoName,
    repoOwner,
    selectedRef,
    selectedRefType,
    setSelectedRef,
    setSelectedRefType,
  }) {
    const { data: refsData, loading: refsDataLoading } = useGetRefsQuery({
      variables: {
        orgId,
        repoId: repoId ?? null,
        repoName: repoName ?? "",
        repoOwner: repoOwner ?? "",
        refType: selectedRefType,
        connectionType,
      },
      skip: selectedRefType === GithubRefType.COMMIT || !repoName || !repoOwner,
    });
    useEffect(() => {
      if (refsData?.getGithubRefs.length === 0) {
        setSelectedRef(undefined);
      } else if (
        !selectedRef &&
        defaultBranch &&
        selectedRefType === GithubRefType.BRANCH &&
        refsData?.getGithubRefs?.map((ref) => ref.name).includes(defaultBranch)
      ) {
        setSelectedRef({ name: defaultBranch } as GithubRef);
      }
    }, [
      defaultBranch,
      setSelectedRef,
      repoName,
      selectedRefType,
      selectedRef,
      refsData,
    ]);

    const onSelectRefType = useCallback(
      (refType: GithubRefType) => {
        setSelectedRef(undefined);
        setSelectedRefType(refType);
      },
      [setSelectedRefType, setSelectedRef],
    );
    const refTypeRenderer: ItemRenderer<GithubRefType> = useCallback(
      (refType, itemProps) => (
        <HexMenuItem
          key={refType}
          active={itemProps.modifiers.active}
          disabled={itemProps.modifiers.disabled}
          icon={REF_TYPE_ICON_MAP[refType]}
          text={sentenceCase(refType)}
          onClick={itemProps.handleClick}
        />
      ),
      [],
    );
    const refPredicate: ItemPredicate<GithubRef> = useCallback((query, ref) => {
      const lowerQuery = query.toLowerCase();
      return ref.name.toLowerCase().indexOf(lowerQuery) > -1;
    }, []);
    const refRenderer: ItemRenderer<GithubRef> = useCallback(
      (ref, itemProps) => (
        <HexMenuItem
          key={ref.name}
          active={itemProps.modifiers.active}
          disabled={itemProps.modifiers.disabled}
          text={ref.name}
          onClick={itemProps.handleClick}
        />
      ),
      [],
    );
    const onSelectRef = useCallback(
      (ref: GithubRef) => {
        setSelectedRef(ref);
      },
      [setSelectedRef],
    );
    const createNewRefFromQuery = useCallback(
      (query: string) => {
        return {
          name: query,
          sha: "",
          refType: selectedRefType,
        } as GithubRef;
      },
      [selectedRefType],
    );

    const createNewRefRenderer = useCallback(
      (
        query: string,
        active: boolean,
        handleClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
      ) => {
        return (
          <HexMenuItem
            key={query}
            active={active}
            text={
              <AddContainer>
                <AddIcon icon="add" />
                <>{`Add ${query}`}</>
              </AddContainer>
            }
            onClick={handleClick}
          />
        );
      },
      [],
    );
    const setCommitCallback = useCallback(
      (evt: React.FormEvent<HTMLInputElement>) =>
        setSelectedRef({
          name: evt.currentTarget.value,
          sha: "",
          refType: selectedRefType,
        } as GithubRef),
      [selectedRefType, setSelectedRef],
    );
    const noRefs = refsData && refsData.getGithubRefs.length == 0;
    return (
      <FormGroup
        label={
          connectionType == VcsConnectionType.GITHUB ? "Ref" : "Main branch"
        }
      >
        <SelectContainer>
          {connectionType == VcsConnectionType.GITHUB && (
            <Select<GithubRefType>
              filterable={false}
              itemRenderer={refTypeRenderer}
              items={REF_TYPES}
              popoverProps={{ minimal: true }}
              resetOnClose={true}
              onItemSelect={onSelectRefType}
            >
              <RefTypeButton
                disabled={!repoName || repoError || repoLoading}
                icon={REF_TYPE_ICON_MAP[selectedRefType]}
                rightIcon={<SingleChevronDownIcon />}
                text={sentenceCase(selectedRefType)}
              />
            </Select>
          )}
          <RightSelectContainer>
            {selectedRefType === GithubRefType.COMMIT && (
              <HexInputGroup
                leftElement={
                  (selectedRef?.name ?? "").length >= SHORT_SHA_LENGTH ? (
                    <CommitStatusContainer>
                      {refDataLoading && <StyledSpinner />}
                      {!refDataLoading && (
                        <HexTooltip
                          content="Commit not found"
                          disabled={!!selectedRef?.sha}
                        >
                          {selectedRef?.sha ? <SuccessIcon /> : <ErrorIcon />}
                        </HexTooltip>
                      )}
                    </CommitStatusContainer>
                  ) : undefined
                }
                placeholder="Enter commit SHA"
                onChange={setCommitCallback}
              />
            )}
            {(selectedRefType === GithubRefType.BRANCH ||
              selectedRefType === GithubRefType.TAG) && (
              <Select<GithubRef>
                createNewItemFromQuery={
                  refsData?.getGithubRefs.length == MAX_REFS
                    ? createNewRefFromQuery
                    : undefined
                }
                createNewItemRenderer={
                  refsData?.getGithubRefs.length == MAX_REFS
                    ? createNewRefRenderer
                    : undefined
                }
                disabled={
                  refsDataLoading || !repoName || repoError || repoLoading
                }
                inputProps={{
                  placeholder:
                    refsData?.getGithubRefs.length == MAX_REFS
                      ? "Filter or add..."
                      : "Filter...",
                }}
                itemPredicate={refPredicate}
                itemRenderer={refRenderer}
                items={refsData?.getGithubRefs.slice() ?? []}
                matchTargetWidth={true}
                popoverProps={{ minimal: true, fill: true }}
                onItemSelect={onSelectRef}
              >
                <SelectButton
                  disabled={!repoName || repoError || repoLoading || noRefs}
                  fill={true}
                  loading={refsDataLoading}
                  rightIcon={<SingleChevronDownIcon />}
                  text={
                    <InnerButtonContainer>
                      {refDataLoading && <StyledSpinner />}
                      {selectedRef && !refDataLoading && (
                        <HexTooltip
                          content={`${
                            selectedRefType === GithubRefType.BRANCH
                              ? "Branch"
                              : "Tag"
                          } not found`}
                          disabled={!refFetchError}
                        >
                          {selectedRef?.sha == "" && refFetchError ? (
                            <ErrorIcon />
                          ) : (
                            <SuccessIcon />
                          )}
                        </HexTooltip>
                      )}
                      <Text ellipsize={true}>
                        {selectedRef
                          ? selectedRef.name
                          : noRefs
                          ? `No ${
                              selectedRefType === GithubRefType.BRANCH
                                ? "branches"
                                : "tags"
                            }`
                          : "Select..."}
                      </Text>
                    </InnerButtonContainer>
                  }
                />
              </Select>
            )}
          </RightSelectContainer>
        </SelectContainer>
        {noRefs && (
          <NoBranchesCallout intent={Intent.WARNING}>
            This repository has no{" "}
            {selectedRefType === GithubRefType.BRANCH ? "branches" : "tags"}.
            Create one on {vcsTypeToDisplayName(connectionType)} to continue
            with setup.
          </NoBranchesCallout>
        )}
      </FormGroup>
    );
  });
